import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';
import {
  mediaQueryLarge,
  prefersReducedMotion,
  requestIdleCallback,
  startViewTransition,
  supportsViewTransitions,
} from '@theme/utilities';

const OTHER_GROUP_KEY = '__other__';

/**
 * A custom element for the location-grouped collection section
 * (`main-collection-shop-groups.liquid`).
 *
 * The section paginates `collection.products`, so one page holds only the top
 * slice of the ranked product list, grouped by shop location — or, for
 * shipping-only products (no pickup location), by shop. "Show more" fetches
 * the next page of this very section through the Section Rendering API and
 * merges it into the page instead of appending it verbatim:
 *
 * - items of a group already on the page are appended to that group,
 * - unseen groups are appended as new groups (before the trailing
 *   "Other products" group, which always stays last).
 *
 * Both kinds of group merge identically — the keys differ (a location handle
 * vs. `__shop__<shop-handle>`) but the rule does not. Because every group
 * already on the page had a better-ranked product on an earlier page,
 * appending new groups at the end preserves the section's group order: groups
 * ranked by their best-ranked product. Without JavaScript the "Show more"
 * anchor is a plain link to `?page=N`.
 *
 * The click handler is delegated and the auto-load observer re-arms on
 * filter/sort updates, because the facets machinery morphs the section's DOM
 * (replacing the load-more nodes) without reconnecting this element.
 *
 * The filters block's grid density controls call `updateLayout` here (their
 * `on:change` targets the closest results container). Unlike `results-list`,
 * which owns a single grid, this section has one grid per location group, so
 * the chosen `product-grid-view` is fanned out to every group grid — and
 * re-fanned whenever a merge or a facet morph brings in fresh
 * server-rendered grids, which always arrive as `default`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [groupsContainer] - The container of the groups.
 * @property {HTMLElement} [loadMoreWrapper] - The wrapper of the "Show more" link.
 * @property {HTMLAnchorElement} [loadMore] - The "Show more" link.
 *
 * @extends Component<Refs>
 */
class ShopGroupsComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #autoLoadObserver;

  #busy = false;

  /** @type {string} The grid density view currently applied to the group grids. */
  #layout = 'default';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleResultsUpdate);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleResultsUpdate);
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

    // The section's pre-paint restore script may have applied a stored
    // density before this module ran — adopt whatever the DOM shows.
    this.#layout = this.querySelector('.shop-group .resource-list--grid')?.getAttribute('product-grid-view') ?? 'default';

    this.#armAutoLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleResultsUpdate);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleResultsUpdate);
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#autoLoadObserver?.disconnect();
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Starts (or restarts) observing the current load-more wrapper for
   * auto-loading. Safe to call whenever the DOM may have been replaced.
   */
  #armAutoLoad() {
    this.#autoLoadObserver?.disconnect();

    const { loadMoreWrapper } = this.refs;
    if (this.dataset.autoLoad !== 'true' || !loadMoreWrapper) return;

    this.#autoLoadObserver ??= new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#loadNextPage();
        }
      },
      { rootMargin: '400px 0px' }
    );
    this.#autoLoadObserver.observe(loadMoreWrapper);
  }

  /**
   * Delegated click handler, so a facets-driven DOM morph that replaces the
   * "Show more" node cannot detach the behavior.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[ref="loadMore"]') : null;
    if (!target || !this.contains(target)) return;

    event.preventDefault();
    this.#loadNextPage();
  };

  /**
   * After a filter or sort update the facets machinery replaced the section's
   * content with a fresh page 1 — re-arm the observer on the new nodes and
   * re-apply the chosen density (the fresh grids render as `default`; the
   * radios themselves survive the morph via `data-skip-node-update`).
   */
  #handleResultsUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;

    // Refs update via the component's own MutationObserver; arm on the next
    // frame so they point at the fresh DOM.
    requestAnimationFrame(() => {
      this.#armAutoLoad();
      if (this.#layout !== 'default') this.#setLayout(this.#layout);
    });
  };

  /**
   * Applies the grid density chosen through the filters block's density
   * controls. Same contract as `ResultsList.updateLayout` — the controls'
   * `on:change` resolves to the closest results container, which is this
   * component on the grouped template.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Animates the layout change and persists it for the current viewport,
   * sharing the standard template's sessionStorage keys so the chosen
   * density follows the visitor across collection pages of either template.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const cleanupCardTransition = this.#prepareCardTransition();
    await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    cleanupCardTransition();

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * The theme's 'product-grid' view-transition prep (utilities.js) only
   * reaches the standard template's `.product-grid` markup, so the group
   * grids' items never get view-transition-names from it and the density
   * switch snapped instead of morphing the cards. This is its analog for the
   * inlined resource-list grids: every item near the viewport gets a unique
   * view-transition-name — unique per ITEM, not per product, because a
   * product carried at several locations sits in several groups and
   * duplicate names abort the whole transition — and off-screen items are
   * excluded from snapshotting, like the standard grid's overflow cards.
   *
   * @returns {() => void} Removes the inline properties again.
   */
  #prepareCardTransition() {
    if (!supportsViewTransitions() || prefersReducedMotion()) return () => {};

    const items = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('.shop-group .resource-list--grid .resource-list__item'),
    ]);
    if (items.length === 0) return () => {};

    // Each named element is snapshotted individually; keep the count bounded
    // even on a tall viewport full of zoomed-out cards. A visible item past
    // the cap is left untouched (it crossfades with the root) — hiding it
    // would leave a hole in the old snapshot.
    const maxAnimatedCards = 60;
    let named = 0;

    for (const item of items) {
      const rect = item.getBoundingClientRect();
      const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;

      if (inViewport && named < maxAnimatedCards) {
        item.style.setProperty('view-transition-name', `shop-group-card-${named}`);
        named += 1;
      } else if (!inViewport) {
        item.style.setProperty('content-visibility', 'hidden');
      }
    }

    return () => {
      for (const item of items) {
        item.style.removeProperty('view-transition-name');
        item.style.removeProperty('content-visibility');
      }
    };
  }

  /**
   * Sets the given view on every group grid.
   *
   * @param {string} value
   */
  #setLayout(value) {
    this.#layout = value;

    for (const grid of this.querySelectorAll('.shop-group .resource-list--grid')) {
      grid.setAttribute('product-grid-view', value);
    }
  }

  /**
   * Crossing the mobile/desktop breakpoint resets the density to `default`,
   * because each viewport has its own set of options (and stored value).
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  async #loadNextPage() {
    const { loadMore, groupsContainer } = this.refs;

    if (this.#busy || !loadMore || !groupsContainer) return;

    this.#busy = true;
    loadMore.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(loadMore.href, location.origin);
      // buildSectionRenderingURL mutates the URL it is given (it bakes
      // `section_id` into it), so the renderer gets a throwaway clone and
      // `url` stays clean for the history entry.
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, new URL(url));
      if (!html) return;

      const incomingDocument = new DOMParser().parseFromString(html, 'text/html');
      const progressed = this.#mergeGroups(incomingDocument);
      // Grids of newly appended groups arrive as `default` from the server.
      if (progressed && this.#layout !== 'default') this.#setLayout(this.#layout);
      this.#advanceLoadMore(incomingDocument, url, progressed);
    } catch (error) {
      console.error('Could not load more shop groups', error);
    } finally {
      this.#busy = false;
      // The wrapper may have been removed by #advanceLoadMore on the last page.
      this.refs.loadMore?.removeAttribute('aria-busy');
    }
  }

  /**
   * Merges the groups of a freshly fetched page into the current page.
   *
   * @param {Document} incomingDocument - The parsed section HTML of the next page.
   * @returns {boolean} Whether the merge added any group or product to the page.
   */
  #mergeGroups(incomingDocument) {
    const { groupsContainer } = this.refs;
    if (!groupsContainer) return false;

    let progressed = false;

    for (const incomingGroup of incomingDocument.querySelectorAll('[data-shop-group]')) {
      const key = incomingGroup.getAttribute('data-shop-group');
      if (!key) continue;

      const existingGroup = groupsContainer.querySelector(`[data-shop-group="${CSS.escape(key)}"]`);

      if (!existingGroup) {
        // A group first seen on this page (a shop location, or a shop's
        // shipping-only group): its best-ranked product ranks below every
        // group already on the page, so appending keeps the group order
        // correct. The synthetic "Other products" group always stays last.
        const otherGroup = groupsContainer.querySelector(`[data-shop-group="${OTHER_GROUP_KEY}"]`);
        if (key !== OTHER_GROUP_KEY && otherGroup) {
          otherGroup.before(incomingGroup);
        } else {
          groupsContainer.append(incomingGroup);
        }
        progressed = true;
        continue;
      }

      const targetList = existingGroup.querySelector('.resource-list');
      if (!targetList) continue;

      // A product can legitimately sit in several location groups, but within
      // one group it must appear only once — and pagination boundaries can
      // shift between requests.
      const seen = new Set(
        Array.from(existingGroup.querySelectorAll('.resource-list__item'), (item) =>
          item.getAttribute('data-product-id')
        )
      );

      for (const item of incomingGroup.querySelectorAll('.resource-list__item')) {
        const productId = item.getAttribute('data-product-id');
        if (productId && seen.has(productId)) continue;
        // The same card node can only live in one group; clone it so every
        // group carrying the product gets its copy.
        targetList.append(item.cloneNode(true));
        seen.add(productId);
        progressed = true;
      }
    }

    return progressed;
  }

  /**
   * Points the "Show more" link at the following page, or removes it when the
   * merged page was the last one.
   *
   * @param {Document} incomingDocument - The parsed section HTML of the merged page.
   * @param {URL} url - The URL of the merged page (without renderer params).
   * @param {boolean} progressed - Whether the merge added anything to the page.
   */
  #advanceLoadMore(incomingDocument, url, progressed) {
    const { loadMore, loadMoreWrapper } = this.refs;
    const incomingLoadMore = incomingDocument.querySelector('[ref="loadMore"]');
    const nextHref = incomingLoadMore?.getAttribute('href');

    if (nextHref && loadMore) {
      loadMore.href = nextHref;

      // Re-observe so the observer reports the current intersection state:
      // if the wrapper is still in view after the merge (short page), no
      // threshold is crossed and auto-loading would stall otherwise. Only
      // while pages actually add content — a page that yields nothing means
      // something is off (or pure duplicates), and auto-repeating would race
      // through every remaining page. The manual button stays functional.
      if (progressed) {
        this.#armAutoLoad();
      }
    } else {
      this.#autoLoadObserver?.disconnect();
      loadMoreWrapper?.remove();
    }

    // Mirrors the theme's infinite scroll: the URL reflects the last loaded
    // page, so reload/back lands close to where the visitor was. Never with
    // renderer params (a `section_id` URL renders the bare section), and only
    // when the page visibly changed.
    if (progressed) {
      url.searchParams.delete('section_id');
      history.pushState('', '', url.toString());
    }
  }
}

if (!customElements.get('shop-groups-component')) {
  customElements.define('shop-groups-component', ShopGroupsComponent);
}
